/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges,
} from '@angular/core';
import { TranslationService } from '../../common/translation.service';

/**
 * Enumeration of the type of button.
 */
export enum ButtonStyle {
  ACTION = 'ACTION',
  ALERT = 'ALERT',
  GREY = 'GREY',
  INACTIVE = 'INACTIVE',
  NEGATIVE_ACTION = 'NEGATIVE_ACTION',
  OUTLINE = 'OUTLINE',
  PRIMARY = 'PRIMARY',
  PRIMARY_2 = 'PRIMARY_2',
  SAVE = 'SAVE',
  SECONDARY = 'SECONDARY',
  TEXT = 'TEXT',
  TEXT_GREY = 'TEXT_GREY',
  TEXT_WITH_ICON = 'TEXT_WITH_ICON',
}

/**
 * Enumeration for the size of the button.
 */
export enum ButtonSize {
  LARGE = 'LARGE',
  PILL = 'PILL',
  SLIM = 'SLIM',
  LARGER = 'LARGER',
}

/**
 * Component for our aero-button.
 */
@Component({
  selector: 'aero-button',
  templateUrl: './aero-button.component.html',
  standalone: false,
})
export class AeroButtonComponent implements OnChanges, OnInit {
  /**
   * Style of button. Default value is PRIMARY.
   */
  @Input() public aeroStyle : ButtonStyle = ButtonStyle.PRIMARY;

  /**
   * Size of button. Default value is LARGE.
   */
  @Input() public size? : ButtonSize = ButtonSize.LARGE;

  /**
   * Used to apply a custom class to the button
   */
  @Input() public styleClass? : string;

  /**
   * Optional label for the button.
   */
  @Input() public label? : string;

  /**
   * Optional translation code to use for the label of the button.
   * A value provided for label will override this code.
   */
  @Input() labelCode? : string;

  /**
   * Optional Icon for the button.
   */
  @Input() public icon? : string;

  /**
   * Optional SVG Icon key for the button.
   */
  @Input() public svgIconKey? : string;

  /**
   * Optional disabled attribute for the button
   */
  @Input() public disabled? : boolean;

  /**
   * Optional AngularJs callback
   */
  @Input() public callback? : Function;

  /**
   * The HTML type of the button: 'button', 'reset', or 'submit'. Default value is button.
   */
  @Input() public type? : string = 'button';

  /**
   * click handler for button
   */
  @Output() public onClick?: EventEmitter<any> = new EventEmitter<any>();

  public translatedLabel : string;

  /**
   * Computed styleClass for the component
   */
  public componentStyle: string;

  /**
   * Default Constructor, for which we inject our AngularJS translation service.
   *
   * @param $translate AngularJS translation service.
   */
  constructor(private $translate : TranslationService) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    this.updateLabel();
    this.createStyleClass();
  }

  /**
   * A lifecycle hook that is called when any data-bound property of a component changes.
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) : void {
    if (changes.label || changes.labelCode) {
      this.updateLabel();
    }
    if (changes.aeroStyle || changes.styleClass || changes.size || changes.icon) {
      this.createStyleClass();
    }
  }

  /**
   * Helper method to set the button label based on whether a label or label code is provided.
   * @private
   */
  private updateLabel() {
    if (this.label) {
      this.translatedLabel = this.label;
    } else {
      this.translatedLabel = (this.labelCode) ? this.$translate.translateInstant(this.labelCode)
        : this.$translate.translateInstant('button_default_label');
    }
  }

  /**
   * Click handler for button
   *
   * @param event
   */
  public handleClick(event: Event) {
    this.onClick.emit(event);
  }

  public createStyleClass() {
    if (this.size && this.aeroStyle && this.icon) {
      this.componentStyle = `${this.aeroStyle} ${this.size} aero-icon`;
    } else if (this.size && this.aeroStyle && this.styleClass) {
      this.componentStyle = `${this.size} ${this.aeroStyle} ${this.styleClass}`;
    } else if (this.size && this.aeroStyle) {
      this.componentStyle = `${this.size} ${this.aeroStyle}`;
    } else if (this.size && this.styleClass) {
      this.componentStyle = `${this.size} ${this.styleClass}`;
    } else if (this.aeroStyle && this.styleClass) {
      this.componentStyle = `${this.aeroStyle} ${this.styleClass}`;
    } else if (this.styleClass) {
      this.componentStyle = `${this.styleClass}`;
    } else if (this.aeroStyle) {
      this.componentStyle = `${this.aeroStyle}`;
    } else {
      this.componentStyle = ButtonSize.LARGE;
    }
  }
}
